/**
 * Dummy Mode Hook
 * 
 * Checks if dummy mode is enabled via query parameter `isDummyMode=true`
 * When enabled via query param, stores it in Redux for persistence across navigation.
 * Reads from Redux state to preserve dummy mode when navigating between routes.
 */

'use client';

import { useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setDummyMode } from '@/store/slices/dummyModeSlice';

// Module-level tracking to prevent infinite dispatches across remounts
let lastSyncedQueryParam: string | null = null;
let lastSyncedStoreValue: boolean | null = null;

export function useDummyMode(): { isDummyMode: boolean } {
  const searchParams = useSearchParams();
  const dispatch = useAppDispatch();
  const isDummyModeFromStore = useAppSelector((state) => state.dummyMode.isDummyMode);
  const isAuthenticated = useAppSelector((state) => state.user.isAuthenticated);
  
  // Check query parameter (can be 'true' or 'false', or not present)
  const queryParam = searchParams?.get('isDummyMode');
  const isDummyModeFromQuery = queryParam === 'true';
  const hasQueryParam = queryParam !== null; // Query param is explicitly set

  // Sync query parameter with Redux store
  useEffect(() => {
    // Skip sync if authenticated to prevent re-renders during redirect
    if (isAuthenticated) {
      return;
    }
    
    // Only dispatch if query param is explicitly set AND differs from store AND we haven't synced this exact combination
    // This prevents unnecessary dispatches on every render
    const needsSync = hasQueryParam && 
                      isDummyModeFromQuery !== isDummyModeFromStore &&
                      (lastSyncedQueryParam !== queryParam || lastSyncedStoreValue !== isDummyModeFromStore);
    
    if (needsSync) {
      lastSyncedQueryParam = queryParam;
      lastSyncedStoreValue = isDummyModeFromQuery;
      // If query param is explicitly set, update Redux store
      // This allows enabling/disabling dummy mode via URL and persisting it
      dispatch(setDummyMode(isDummyModeFromQuery));
    } else if (!hasQueryParam) {
      // Reset tracking when query param is removed
      lastSyncedQueryParam = null;
      lastSyncedStoreValue = null;
    }
  }, [hasQueryParam, isDummyModeFromQuery, isDummyModeFromStore, dispatch, queryParam, isAuthenticated]);

  // Use query param if explicitly set, otherwise use stored value
  const isDummyMode = hasQueryParam ? isDummyModeFromQuery : isDummyModeFromStore;

  return { isDummyMode };
}
